import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import {
    Comment,
    Comments,
    CommentTargetForFollowUpAction
} from '../../../../backbone-unorganized/shared/comments/models';
import { LegacyApiModel } from '../types';
import { PostMention, TrackedMention } from './mentionText.utils';

export interface DisplayComment {
    id: number;
    message: string;
    dateDisplayed: string;
    displayName: string;
    userRole: string;
    isActive: boolean;
    canDelete: boolean;
    createdUserId?: number;
    mentions: TrackedMention[];
    model: LegacyApiModel & {
        delete: (options: { success: () => void; error: () => void }) => void;
    };
}

const getMentionUserRole = (userRole: unknown): string | null => {
    if (typeof userRole === 'string') {
        return userRole || null;
    }
    if (userRole && typeof userRole === 'object' && 'name' in userRole) {
        return (userRole as { name?: string }).name ?? null;
    }
    return null;
};

const toDisplayComment = (model: DisplayComment['model'], activeUserId?: number): DisplayComment => {
    const createdUser =
        (model.get('created_user') as {
            id?: number;
            display_name?: string;
            user_role?: { name?: string };
        } | null) ?? {};
    const isActive = model.get('is_active') !== false;
    const mentionsCollection = model.get('mentions') as
        | { map: (fn: (m: LegacyApiModel) => TrackedMention) => TrackedMention[] }
        | undefined;

    return {
        id: model.get('id') as number,
        message: (model.get('message') as string | null) ?? '',
        dateDisplayed: moment(model.get('date_created') as string).format('MMM D, YYYY h:mma'),
        displayName: createdUser.display_name ?? '',
        userRole: createdUser.user_role?.name ?? '',
        isActive,
        canDelete: isActive && createdUser.id !== undefined && createdUser.id === activeUserId,
        createdUserId: createdUser.id,
        mentions:
            mentionsCollection?.map((mention) => ({
                id: mention.get('id') as number,
                displayName: (mention.get('display_name') as string | null) ?? '',
                userRole: getMentionUserRole(mention.get('user_role')),
                isSubscriber: Boolean(mention.get('is_subscriber'))
            })) ?? [],
        model
    };
};

interface BaseSubscriber {
    id?: number | string;
    display_name?: string;
}

export const useFollowUpActionComments = (
    followUpActionId: number,
    enabled: boolean,
    baseSubscribers: BaseSubscriber[] = []
) => {
    const { enqueueSnackbar } = useSnackbar();
    const activeUserId = $f.active_user?.get('id') as number | undefined;

    const [collection] = useState(
        () =>
            new Comments(null, {
                target: new CommentTargetForFollowUpAction({ id: followUpActionId })
            })
    );

    const [comments, setComments] = useState<DisplayComment[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [commentPendingDelete, setCommentPendingDelete] = useState<DisplayComment | null>(null);

    const refreshComments = () => {
        setComments(collection.map((model: DisplayComment['model']) => toDisplayComment(model, activeUserId)));
    };

    useEffect(() => {
        if (!enabled) {
            return;
        }

        collection.fetch({
            success: () => {
                refreshComments();
                setLoading(false);
                collection.mark_all_as_read({ success: refreshComments, error: () => {} });
            },
            error: () => {
                setLoading(false);
                enqueueSnackbar(_t('mobile_message_show_error_message'), { variant: 'error' });
            }
        });
    }, [enabled]);

    // Who will be notified: the FUA's creator/assignee (mirrors FollowUpActionTarget.get_target_subscribers on the
    // backend) plus anyone mentioned in or authoring an existing comment — excluding the current user.
    const savedNotifierIds = useMemo(() => {
        const map = new Map<number, TrackedMention>();

        baseSubscribers.forEach((subscriber) => {
            const id = Number(subscriber.id);
            if (!Number.isNaN(id) && id !== activeUserId) {
                map.set(id, { id, displayName: subscriber.display_name ?? '', userRole: null, isSubscriber: true });
            }
        });

        comments.forEach((comment) => {
            comment.mentions.forEach((mention) => {
                if (mention.id !== activeUserId) {
                    map.set(mention.id, mention);
                }
            });
        });

        comments.forEach((comment) => {
            if (comment.isActive && comment.createdUserId !== undefined && comment.createdUserId !== activeUserId) {
                map.set(comment.createdUserId, {
                    id: comment.createdUserId,
                    displayName: comment.displayName,
                    userRole: comment.userRole || null,
                    isSubscriber: false
                });
            }
        });

        return map;
    }, [comments, activeUserId, baseSubscribers]);

    const checkMentionPermission = (userId: number): Promise<boolean> =>
        new Promise((resolve) => {
            collection.target.checkMentionPermission({
                mentioned_user_id: userId,
                success: (canBeMentioned: boolean) => resolve(canBeMentioned),
                error: () => resolve(false)
            });
        });

    const addComment = (message: string, mentions: PostMention[]) => {
        const trimmed = message.trim();
        if (!trimmed || submitting) {
            return;
        }

        setSubmitting(true);
        collection.add_comment(new Comment({ message: trimmed, mentions }), {
            success: () => {
                setSubmitting(false);
                refreshComments();
                enqueueSnackbar(_t('mobile_comments_add_success_message'), { variant: 'success' });
            },
            error: () => {
                setSubmitting(false);
                enqueueSnackbar(_t('mobile_message_show_error_message'), { variant: 'error' });
            }
        });
    };

    const requestDelete = (comment: DisplayComment) => setCommentPendingDelete(comment);
    const cancelDelete = () => setCommentPendingDelete(null);

    const confirmDelete = () => {
        if (!commentPendingDelete) {
            return;
        }

        commentPendingDelete.model.delete({
            success: () => {
                enqueueSnackbar(_t('mobile_comments_delete_success_message'), { variant: 'success' });
                setCommentPendingDelete(null);
                refreshComments();
            },
            error: () => {
                enqueueSnackbar(_t('mobile_message_show_error_message'), { variant: 'error' });
                setCommentPendingDelete(null);
            }
        });
    };

    return {
        comments,
        loading,
        submitting,
        addComment,
        savedNotifierIds,
        checkMentionPermission,
        commentPendingDelete,
        requestDelete,
        cancelDelete,
        confirmDelete
    };
};
